import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import ErrorIcon from '@material-ui/icons/Error';
import HelpIcon from '@material-ui/icons/HelpOutline';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import RefreshIcon from '@material-ui/icons/Refresh';
import { useApi } from '@backstage/core-plugin-api';
import { PrFileDiff, PrSummary, TerraformPlan } from '../../api/types';
import { PrDescription } from './PrDescription';
import { isTerraform } from '../EntityGroupMergeOpsContent/filter';
import { mergeOpsApiRef } from '../../api/MergeOpsApi';
import {
  actionErrorMessage,
  mergeBlockReason,
  NOT_OWNER_REASON,
  usePrActions,
} from '../../hooks/usePrActions';
import { useNow } from '../../hooks/useNow';
import { cloudBuildUrl, cloudDeployUrl } from '../../links';
import { relativeAge } from '../../relativeTime';
import { stageOf } from '../../stages';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(1.25, 2),
    display: 'grid',
    // Fixed stage column so every row's BUILD / DEPLOY XXX lines up as a column.
    gridTemplateColumns: '144px minmax(0, 1fr) auto',
    gridTemplateAreas: `
      "stage title actions"
      "stage meta actions"
    `,
    rowGap: theme.spacing(0.5),
    columnGap: theme.spacing(2),
    alignItems: 'center',
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(39, 40, 44, 0.46)'
        : 'rgba(255, 255, 255, 0.72)',
    transition: theme.transitions.create('background-color', {
      duration: theme.transitions.duration.shortest,
    }),
    '&:hover': {
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(20, 184, 166, 0.06)'
          : 'rgba(13, 148, 136, 0.04)',
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      gridTemplateAreas: `
        "stage"
        "title"
        "meta"
        "actions"
      `,
    },
  },
  rootCompact: {
    padding: theme.spacing(1, 1.25),
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gridTemplateAreas: `
      "title actions"
      "meta actions"
    `,
    columnGap: theme.spacing(1.25),
    rowGap: theme.spacing(0.25),
  },
  rootSelectable: {
    gridTemplateColumns: '40px 144px minmax(0, 1fr) auto',
    gridTemplateAreas: `
      "select stage title actions"
      "select stage meta actions"
    `,
  },
  rootCompactSelectable: {
    gridTemplateColumns: '40px minmax(0, 1fr) auto',
    gridTemplateAreas: `
      "select title actions"
      "select meta actions"
    `,
  },
  rootToggle: {
    cursor: 'pointer',
    '&:hover $titleText': {
      color: theme.palette.primary.main,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
    },
  },
  selectCell: {
    gridArea: 'select',
    alignSelf: 'center',
    justifySelf: 'center',
    '& .MuiCheckbox-root': {
      padding: theme.spacing(0.75),
    },
  },
  title: {
    gridArea: 'title',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    minWidth: 0,
  },
  titleToggle: {
    gridArea: 'title',
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
    width: '100%',
    padding: 0,
    border: 0,
    background: 'transparent',
    color: theme.palette.text.primary,
    cursor: 'pointer',
    textAlign: 'left',
    '&:hover $titleText': {
      color: theme.palette.primary.main,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
  titleToggleIcon: {
    color: theme.palette.text.secondary,
    fontSize: 20,
    flex: '0 0 auto',
    justifySelf: 'end',
  },
  titleContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    minWidth: 0,
  },
  stageTag: {
    gridArea: 'stage',
    alignSelf: 'center',
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: 5,
    fontSize: '0.8rem',
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  stageEnv: { color: theme.palette.text.primary },
  hiddenCompact: {
    display: 'none',
  },
  prNumber: {
    fontWeight: 600,
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  titleText: {
    fontWeight: 600,
    minWidth: 0,
    overflowWrap: 'anywhere',
    letterSpacing: 0,
  },
  titleTextCompact: {
    fontSize: '0.9rem',
  },
  meta: {
    gridArea: 'meta',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    minWidth: 0,
  },
  metaCompact: {
    gap: theme.spacing(1),
    fontSize: '0.75rem',
  },
  metaItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minWidth: 0,
    height: 24,
    padding: theme.spacing(0, 0.75),
    borderRadius: 999,
    border: `1px solid ${
      theme.palette.type === 'dark'
        ? 'rgba(196, 196, 204, 0.2)'
        : 'rgba(15, 23, 42, 0.1)'
    }`,
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(35, 36, 40, 0.5)'
        : 'rgba(248, 250, 252, 0.82)',
    color: theme.palette.text.secondary,
  },
  // Sits in the meta row and wears the metaItem pill (both classes are
  // applied); this only strips the Button chrome that would clash with it.
  refreshButton: {
    textTransform: 'none',
    fontSize: 'inherit',
    fontWeight: 400,
    lineHeight: 1,
    minWidth: 0,
    '& .MuiButton-startIcon': { marginLeft: 0, marginRight: 4 },
  },
  refreshIcon: { fontSize: 14 },
  actions: {
    gridArea: 'actions',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: theme.spacing(0.5),
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    '& .MuiButton-root': {
      height: 32,
      borderRadius: 6,
      fontWeight: 700,
      paddingLeft: theme.spacing(1.25),
      paddingRight: theme.spacing(1.25),
    },
    [theme.breakpoints.down('sm')]: {
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
    },
  },
  mergeBlockedHint: {
    // Always drop onto its own line under the action buttons (rather than
    // hanging off the right edge of the Merge button) and align with them.
    flexBasis: '100%',
    width: '100%',
    marginTop: theme.spacing(0.25),
    fontSize: '0.7rem',
    color: theme.palette.text.hint,
    textAlign: 'right',
    [theme.breakpoints.down('sm')]: {
      textAlign: 'left',
    },
  },
  alert: {
    gridColumn: '1 / -1',
    marginTop: theme.spacing(1),
  },
  fileDiffs: {
    gridColumn: '1 / -1',
    // The row toggles on click (cursor: pointer), but this expanded detail area
    // (description + diff) isn't clickable — reset the inherited pointer so it
    // doesn't look interactive. Links inside still show their own pointer.
    cursor: 'default',
    borderTop: `1px solid ${
      theme.palette.type === 'dark'
        ? 'rgba(196, 196, 204, 0.18)'
        : 'rgba(15, 23, 42, 0.1)'
    }`,
    paddingTop: theme.spacing(1.25),
    marginTop: theme.spacing(0.75),
    display: 'grid',
    gap: theme.spacing(1),
  },
  fileDiffHeader: {
    color: theme.palette.text.primary,
    fontWeight: 700,
    fontSize: '0.84rem',
    marginBottom: theme.spacing(0.25),
  },
  // Loading / error / empty message — roomy plain text rather than the cramped
  // metaItem pill, so it doesn't look squeezed under the header.
  fileDiffStatus: {
    color: theme.palette.text.secondary,
    fontSize: '0.82rem',
    padding: theme.spacing(1, 0.25, 1.25),
  },
  fileDiffRow: {
    border: `1px solid ${
      theme.palette.type === 'dark'
        ? 'rgba(196, 196, 204, 0.2)'
        : 'rgba(15, 23, 42, 0.12)'
    }`,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(30, 31, 35, 0.52)'
        : theme.palette.background.paper,
  },
  fileDiffMeta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1),
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(55, 56, 61, 0.72)'
        : 'rgba(248, 250, 252, 0.95)',
    color: theme.palette.text.secondary,
    fontSize: '0.78rem',
    flexWrap: 'wrap',
  },
  fileName: {
    color: theme.palette.text.primary,
    fontFamily: 'monospace',
    overflowWrap: 'anywhere',
  },
  fileStats: {
    fontFamily: 'monospace',
    whiteSpace: 'nowrap',
  },
  patchViewer: {
    maxHeight: 340,
    overflow: 'auto',
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(31, 32, 36, 0.42)'
        : theme.palette.background.paper,
  },
  patchLine: {
    display: 'grid',
    gridTemplateColumns: '48px 48px minmax(0, 1fr)',
    minWidth: 0,
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    lineHeight: 1.45,
    borderTop: `1px solid ${
      theme.palette.type === 'dark'
        ? 'rgba(255,255,255,0.04)'
        : 'rgba(27,31,36,0.06)'
    }`,
  },
  patchAdd: {
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(34, 197, 94, 0.12)'
        : 'rgba(220, 252, 231, 0.72)',
  },
  patchDelete: {
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(248, 113, 113, 0.12)'
        : 'rgba(254, 226, 226, 0.72)',
  },
  patchHunk: {
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(45, 212, 191, 0.1)'
        : 'rgba(204, 251, 241, 0.64)',
    color: theme.palette.primary.main,
  },
  patchContext: {
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(34, 35, 39, 0.36)'
        : theme.palette.background.paper,
  },
  lineNo: {
    padding: theme.spacing(0.15, 0.75),
    color: theme.palette.text.hint,
    textAlign: 'right',
    userSelect: 'none',
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  codeLine: {
    padding: theme.spacing(0.15, 1),
    color: theme.palette.text.primary,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },
  noPatch: {
    padding: theme.spacing(0.75, 1),
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
  iconSuccess: {
    color: theme.palette.success?.main ?? '#2e7d32',
    fontSize: 16,
  },
  iconPending: {
    color: theme.palette.warning?.main ?? '#ed6c02',
    fontSize: 16,
  },
  iconFailure: { color: theme.palette.error.main, fontSize: 16 },
  iconUnknown: { color: theme.palette.text.hint, fontSize: 16 },
  currentTagMono: {
    fontFamily: 'monospace',
    fontSize: '0.85em',
    color: theme.palette.text.secondary,
  },
  pendingArrow: {
    color: theme.palette.text.hint,
    fontFamily: 'monospace',
    marginLeft: theme.spacing(0.5),
  },
}));

type Styles = ReturnType<typeof useStyles>;

interface DiffLine {
  kind: 'add' | 'delete' | 'context' | 'hunk';
  oldLine: number | null;
  newLine: number | null;
  text: string;
}

function parsePatch(patch: string): DiffLine[] {
  let oldLine = 0;
  let newLine = 0;
  return patch.split('\n').map(line => {
    const hunk = line.match(/^@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@/);
    if (hunk) {
      oldLine = Number(hunk[1]);
      newLine = Number(hunk[2]);
      return {
        kind: 'hunk',
        oldLine: null,
        newLine: null,
        text: line,
      };
    }
    if (line.startsWith('+')) {
      const parsed = {
        kind: 'add' as const,
        oldLine: null,
        newLine,
        text: line,
      };
      newLine += 1;
      return parsed;
    }
    if (line.startsWith('-')) {
      const parsed = {
        kind: 'delete' as const,
        oldLine,
        newLine: null,
        text: line,
      };
      oldLine += 1;
      return parsed;
    }
    const parsed = {
      kind: 'context' as const,
      oldLine,
      newLine,
      text: line,
    };
    oldLine += 1;
    newLine += 1;
    return parsed;
  });
}

function patchLineClass(classes: Styles, kind: DiffLine['kind']): string {
  if (kind === 'add') return `${classes.patchLine} ${classes.patchAdd}`;
  if (kind === 'delete') return `${classes.patchLine} ${classes.patchDelete}`;
  if (kind === 'hunk') return `${classes.patchLine} ${classes.patchHunk}`;
  return `${classes.patchLine} ${classes.patchContext}`;
}

const PatchViewer = ({
  patch,
  classes,
}: {
  patch: string | null;
  classes: Styles;
}) => {
  if (!patch) {
    return (
      <Typography className={classes.noPatch}>
        No inline patch available for this file.
      </Typography>
    );
  }
  return (
    <Box className={classes.patchViewer}>
      {parsePatch(patch).map((line, index) => (
        <Box
          key={`${index}:${line.oldLine ?? ''}:${line.newLine ?? ''}`}
          className={patchLineClass(classes, line.kind)}
        >
          <span className={classes.lineNo}>{line.oldLine ?? ''}</span>
          <span className={classes.lineNo}>{line.newLine ?? ''}</span>
          <span className={classes.codeLine}>{line.text}</span>
        </Box>
      ))}
    </Box>
  );
};

const ChecksIndicator = ({
  pr,
  classes,
}: {
  pr: PrSummary;
  classes: Styles;
}) => {
  switch (pr.checks) {
    case 'success':
      return (
        <span className={classes.metaItem}>
          <CheckCircleIcon className={classes.iconSuccess} />
          checks
        </span>
      );
    case 'pending':
      return (
        <span className={classes.metaItem}>
          <HourglassEmptyIcon className={classes.iconPending} />
          checks pending
        </span>
      );
    case 'failure':
      return (
        <span className={classes.metaItem}>
          <ErrorIcon className={classes.iconFailure} />
          checks failed
        </span>
      );
    default:
      return null;
  }
};

const MergeableIndicator = ({
  pr,
  classes,
}: {
  pr: PrSummary;
  classes: Styles;
}) => {
  if (pr.mergeable === true && pr.mergeableState === 'clean') {
    return (
      <span className={classes.metaItem}>
        <CheckCircleIcon className={classes.iconSuccess} />
        mergeable
      </span>
    );
  }
  if (pr.mergeable === false) {
    return (
      <span className={classes.metaItem}>
        <ErrorIcon className={classes.iconFailure} />
        {pr.mergeableState}
      </span>
    );
  }
  return (
    <span className={classes.metaItem}>
      <HelpIcon className={classes.iconUnknown} />
      mergeable: unknown
    </span>
  );
};

const ApprovalsIndicator = ({
  pr,
  classes,
}: {
  pr: PrSummary;
  classes: Styles;
}) => {
  const ok = pr.approvals >= pr.requiredApprovals;
  const Icon = ok ? CheckCircleIcon : HourglassEmptyIcon;
  const iconClass = ok ? classes.iconSuccess : classes.iconPending;
  return (
    <span className={classes.metaItem}>
      <Icon className={iconClass} />
      {pr.approvals}/{pr.requiredApprovals} approvals
    </span>
  );
};

function progressLinks(
  pr: PrSummary,
  projectName: string,
): Array<{ href: string; label: string }> {
  if (pr.prType === 'tag') {
    return [{ href: cloudBuildUrl(projectName), label: 'Cloud Build' }];
  }
  if (pr.env === 'stg' || pr.env === 'prd') {
    return [{ href: cloudDeployUrl(pr.env), label: `Cloud Deploy ${pr.env}` }];
  }
  if (pr.env === 'all') {
    return [
      { href: cloudDeployUrl('stg'), label: 'Cloud Deploy stg' },
      { href: cloudDeployUrl('prd'), label: 'Cloud Deploy prd' },
    ];
  }
  return [];
}

export const PrRow = ({
  pr,
  componentName,
  projectName,
  currentTag,
  onMerged,
  onApproved,
  compact,
  selectable,
  selected,
  onSelectionChange,
  expanded,
  onExpandedChange,
  showApprove,
  showMerge = true,
  canOperate = true,
  fetchedAtMs,
  onRefreshed,
}: {
  pr: PrSummary;
  componentName: string;
  projectName: string;
  currentTag: string | null;
  onMerged: (prNumber: number) => void;
  onApproved?: (prNumber: number) => void;
  compact?: boolean;
  selectable?: boolean;
  selected?: boolean;
  onSelectionChange?: (selected: boolean) => void;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  showApprove?: boolean;
  showMerge?: boolean;
  /** When false the operator isn't a member of the owning team; disable actions. */
  canOperate?: boolean;
  /** When the list this row came from was fetched, for the row's status age. */
  fetchedAtMs?: number;
  /** Hand the parent the freshly read PR so the list reflects the re-check. */
  onRefreshed?: (pr: PrSummary) => void;
}) => {
  const classes = useStyles();
  const [confirmMerge, setConfirmMerge] = useState(false);
  const api = useApi(mergeOpsApiRef);
  // File diffs are fetched lazily the first time the row is expanded, so the
  // list view doesn't pay a listFiles call per PR up front.
  const [files, setFiles] = useState<PrFileDiff[] | undefined>(undefined);
  const [filesState, setFilesState] = useState<'idle' | 'loading' | 'error'>(
    'idle',
  );
  // Tracks which PR's diff is already loaded so re-expanding a row reuses it
  // instead of re-fetching — the diff stays cached until the page is refreshed
  // (or the PR/component changes). `files`/`filesState` are intentionally NOT
  // effect deps: including them would make the setFilesState('loading') below
  // re-run this effect and cancel its own in-flight fetch, leaving it stuck.
  const loadedKeyRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!expanded) return undefined;
    const key = `${componentName}/${pr.number}`;
    // Already fetched this PR's diff in this mount — keep it (cache until refresh).
    if (loadedKeyRef.current === key) return undefined;
    let cancelled = false;
    setFiles(undefined);
    setFilesState('loading');
    api
      .getPrFiles(componentName, pr.number)
      .then(f => {
        if (!cancelled) {
          setFiles(f);
          setFilesState('idle');
          loadedKeyRef.current = key;
        }
      })
      .catch(() => {
        if (!cancelled) setFilesState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [expanded, api, componentName, pr.number]);
  // Terraform PRs additionally show the tfez plan(s), fetched lazily on expand
  // (same cache-until-refresh pattern as the file diff).
  const isTf = isTerraform(projectName);
  const [plans, setPlans] = useState<TerraformPlan[] | undefined>(undefined);
  const [plansState, setPlansState] = useState<'idle' | 'loading' | 'error'>(
    'idle',
  );
  const plansLoadedKeyRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!expanded || !isTf) return undefined;
    const key = `${componentName}/${pr.number}`;
    if (plansLoadedKeyRef.current === key) return undefined;
    let cancelled = false;
    setPlans(undefined);
    setPlansState('loading');
    api
      .getPrPlans(componentName, pr.number)
      .then(p => {
        if (!cancelled) {
          setPlans(p);
          setPlansState('idle');
          plansLoadedKeyRef.current = key;
        }
      })
      .catch(() => {
        if (!cancelled) setPlansState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [expanded, isTf, api, componentName, pr.number]);
  const { busy, error, successMsg, approve, merge } = usePrActions(
    componentName,
    pr,
    {
      onApproved: () => onApproved?.(pr.number),
      onMerged: () => onMerged(pr.number),
    },
  );

  // How old this row's status is, and the control to re-read it. GitHub doesn't
  // push check results to us, so a row can sit on a stale 'pending' until
  // someone refetches — showing the age is what makes that visible.
  const [refreshedAtMs, setRefreshedAtMs] = useState<number | undefined>(
    undefined,
  );
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  // A whole-list refresh can land after this row's own re-check, so take the
  // later of the two rather than always preferring the local stamp.
  const checkedAtMs =
    Math.max(refreshedAtMs ?? 0, fetchedAtMs ?? 0) || undefined;
  const nowMs = useNow(10_000);
  const refreshStatus = async () => {
    setRefreshing(true);
    setRefreshError(null);
    try {
      const fresh = await api.getPr(componentName, pr.number);
      setRefreshedAtMs(Date.now());
      onRefreshed?.(fresh);
    } catch (err) {
      setRefreshError(actionErrorMessage(err));
    } finally {
      setRefreshing(false);
    }
  };
  let refreshLabel = 'check status';
  if (refreshing) {
    refreshLabel = 'checking…';
  } else if (checkedAtMs) {
    refreshLabel = `checked ${relativeAge(checkedAtMs, nowMs)}`;
  }

  // Why the Merge button is disabled, surfaced on hover and as an inline hint.
  // A merge-block (conflicts, missing approvals, …) takes precedence over the
  // ownership check so the actionable reason wins when both apply.
  const mergeBlocked = mergeBlockReason(pr);
  const mergeDisabledReason =
    mergeBlocked ?? (!canOperate ? NOT_OWNER_REASON : null);
  const stage = stageOf(pr);
  const progress = progressLinks(pr, projectName);

  const handleMerge = async () => {
    setConfirmMerge(false);
    await merge();
  };

  const approveComplete = pr.approvals >= pr.requiredApprovals;
  let approveLabel = 'Approve';
  if (approveComplete) {
    approveLabel = 'Approved';
  } else if (busy === 'approve') {
    approveLabel = 'Approving...';
  }
  // The only non-obvious reason the Approve button is disabled: once approved
  // the label already reads "Approved", and `busy` is transient.
  const approveDisabledReason =
    !approveComplete && !canOperate ? NOT_OWNER_REASON : null;
  const toggleExpanded = () => onExpandedChange?.(!expanded);
  const handleRowKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!onExpandedChange) return;
    // Only the row itself toggles via keyboard; ignore keys bubbling from the
    // checkbox / links / action buttons inside it.
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleExpanded();
    }
  };
  const rootClassName = [
    classes.root,
    compact ? classes.rootCompact : '',
    selectable ? classes.rootSelectable : '',
    compact && selectable ? classes.rootCompactSelectable : '',
    onExpandedChange ? classes.rootToggle : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Box
      className={rootClassName}
      onClick={onExpandedChange ? toggleExpanded : undefined}
      onKeyDown={onExpandedChange ? handleRowKeyDown : undefined}
      role={onExpandedChange ? 'button' : undefined}
      tabIndex={onExpandedChange ? 0 : undefined}
      aria-expanded={onExpandedChange ? Boolean(expanded) : undefined}
    >
      {selectable && (
        <Box
          className={classes.selectCell}
          onClick={event => event.stopPropagation()}
        >
          <Checkbox
            checked={Boolean(selected)}
            onChange={(_, checked) => onSelectionChange?.(checked)}
            inputProps={{ 'aria-label': `Select PR #${pr.number}` }}
            size="small"
          />
        </Box>
      )}
      <span
        className={`${classes.stageTag} ${
          compact ? classes.hiddenCompact : ''
        }`}
      >
        {stage.kind}
        {stage.env && <span className={classes.stageEnv}>{stage.env}</span>}
      </span>
      <Box
        className={`${classes.title} ${
          onExpandedChange ? classes.titleToggle : ''
        }`}
      >
        <Box className={classes.titleContent}>
          <Link
            href={pr.url}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.prNumber}
            onClick={event => event.stopPropagation()}
          >
            #{pr.number}
          </Link>
          <Typography
            variant="body1"
            className={`${classes.titleText} ${
              compact ? classes.titleTextCompact : ''
            }`}
            component="span"
          >
            {pr.title}
          </Typography>
        </Box>
        {onExpandedChange &&
          (expanded ? (
            <ExpandMoreIcon className={classes.titleToggleIcon} />
          ) : (
            <ChevronRightIcon className={classes.titleToggleIcon} />
          ))}
      </Box>

      <Box className={`${classes.meta} ${compact ? classes.metaCompact : ''}`}>
        {!compact && currentTag && (
          <span className={classes.metaItem}>
            <span className={classes.currentTagMono}>{currentTag}</span>
            <span className={classes.pendingArrow}>→ pending</span>
          </span>
        )}
        <ChecksIndicator pr={pr} classes={classes} />
        {!compact && <MergeableIndicator pr={pr} classes={classes} />}
        <ApprovalsIndicator pr={pr} classes={classes} />
        {!compact &&
          progress.map(link => (
            <Link
              key={link.label}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.metaItem}
            >
              {link.label}
            </Link>
          ))}
        <Tooltip
          title={
            checkedAtMs
              ? `Status read from GitHub at ${new Date(
                  checkedAtMs,
                ).toLocaleTimeString()} — click to read it again`
              : 'Read this PR\u2019s status from GitHub'
          }
        >
          <span>
            <Button
              size="small"
              className={`${classes.metaItem} ${classes.refreshButton}`}
              disabled={refreshing}
              onClick={event => {
                event.stopPropagation();
                refreshStatus();
              }}
              startIcon={<RefreshIcon className={classes.refreshIcon} />}
              aria-label={`Refresh status of PR #${pr.number}`}
            >
              {refreshLabel}
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Box
        className={classes.actions}
        onClick={event => event.stopPropagation()}
      >
        {showApprove && (
          <Tooltip
            title={approveDisabledReason ?? ''}
            disableHoverListener={!approveDisabledReason}
          >
            <span>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                disabled={busy !== null || approveComplete || !canOperate}
                onClick={approve}
              >
                {approveLabel}
              </Button>
            </span>
          </Tooltip>
        )}
        {showMerge && (
          <>
            <Tooltip
              title={mergeDisabledReason ?? ''}
              disableHoverListener={!mergeDisabledReason}
            >
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  disabled={busy !== null || mergeDisabledReason !== null}
                  onClick={() => setConfirmMerge(true)}
                >
                  {busy === 'merge' ? 'Merging…' : 'Merge'}
                </Button>
              </span>
            </Tooltip>
            {mergeDisabledReason && (
              <Typography className={classes.mergeBlockedHint}>
                {mergeDisabledReason}
              </Typography>
            )}
          </>
        )}
      </Box>

      {expanded && (
        <Box
          className={classes.fileDiffs}
          onClick={event => event.stopPropagation()}
        >
          {pr.body.trim() !== '' && (
            <>
              <Typography className={classes.fileDiffHeader}>
                Description
              </Typography>
              <PrDescription body={pr.body} />
            </>
          )}
          {isTf &&
            (() => {
              if (plansState === 'error') {
                return (
                  <Typography className={classes.fileDiffStatus}>
                    Failed to load plan.
                  </Typography>
                );
              }
              if (plans === undefined) {
                return (
                  <Typography className={classes.fileDiffStatus}>
                    Loading plan…
                  </Typography>
                );
              }
              if (plans.length === 0) {
                return (
                  <Typography className={classes.fileDiffStatus}>
                    No terraform plan comment found.
                  </Typography>
                );
              }
              return plans.map(plan => (
                <div key={plan.path}>
                  <Typography className={classes.fileDiffHeader}>
                    Terraform plan · {plan.path}
                  </Typography>
                  <PrDescription body={plan.body} />
                </div>
              ));
            })()}
          <Typography className={classes.fileDiffHeader}>File diff</Typography>
          {(() => {
            if (filesState === 'error') {
              return (
                <Typography className={classes.fileDiffStatus}>
                  Failed to load diff.
                </Typography>
              );
            }
            if (files === undefined) {
              return (
                <Typography className={classes.fileDiffStatus}>
                  Loading diff…
                </Typography>
              );
            }
            if (files.length === 0) {
              return (
                <Typography className={classes.fileDiffStatus}>
                  No changed files.
                </Typography>
              );
            }
            return files.map(file => (
              <Box key={file.filename} className={classes.fileDiffRow}>
                <Box className={classes.fileDiffMeta}>
                  <span className={classes.fileName}>{file.filename}</span>
                  <span className={classes.fileStats}>
                    {file.status} +{file.additions} -{file.deletions}
                  </span>
                </Box>
                <PatchViewer patch={file.patch} classes={classes} />
              </Box>
            ));
          })()}
        </Box>
      )}

      {error && (
        <Alert severity="error" className={classes.alert}>
          {actionErrorMessage(error)}
        </Alert>
      )}
      {refreshError && (
        <Alert severity="error" className={classes.alert}>
          {refreshError}
        </Alert>
      )}
      {successMsg && (
        <Alert severity="success" className={classes.alert}>
          {successMsg}
        </Alert>
      )}

      <Dialog open={confirmMerge} onClose={() => setConfirmMerge(false)}>
        <DialogTitle>Merge #{pr.number}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will squash-merge <strong>{pr.title}</strong> into{' '}
            <code>{pr.base}</code> as the kw-vega GitHub App, attributed to you
            in the commit message.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmMerge(false)}>Cancel</Button>
          <Button onClick={handleMerge} color="primary" variant="contained">
            Merge
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
